import { isAthlete, isCoach } from '@/helpers/rolesHelper';
import {
    isAthleteSelected,
    isCrewSelected,
    sessionHasResults,
    sessionHasHrData,
    sessionHasGpsData
} from '@/helpers/sessionResultsHelper';
import get from 'get-object-path';
import Vue from 'vue';
import { i18n } from '@/lang';

export const sessionResults = {
    namespaced: true,

    state: {
        session: null,
        athletesHrFiles: [],
        athletesGpsFiles: [],
        crewsGpsFiles: [],
        crewsPeachFiles: [],
        crewsDataTimestamps: {}, // Store timestamps here
        individualUserId: null
    },

    getters: {
        session(state) {
            return state.session;
        },

        sessionName(state) {
            return get(state, 'session.name');
        },

        sessionId(state) {
            return get(state, 'session.id');
        },

        sessionPlannedRpe(state) {
            return get(state, 'session.planned_rpe');
        },

        sessionIntensities(state) {
            return _.sortBy(get(state, 'session.intensities.data') || [], 'order');
        },

        sessionHrIntensities(state) {
            return Object.values(get(state, 'session.heart_rate_intensities.data') || {});
        },

        sessionSrIntensities(state) {
            return Object.values(get(state, 'session.stroke_rate_intensities.data') || {});
        },

        sportId(state) {
            return get(state, 'session.sport.id');
        },

        canAddScores(state) {
            return get(state, 'session.user_actions.data.add_all_scores');
        },

        competitiveType(state) {
            return get(state, 'session.competitive');
        },

        piecesCount(state) {
            return get(state, 'session.session.data.structure.data[0].pieces_count');
        },

        pieces(state) {
            return get(state, 'session.session.data.structure.data[0].pieces.data') || [];
        },

        sessionCrews(state) {
            return get(state, 'session.crews.data') || [];
        },

        hasTimePiece(state, getters) {
            return getters.pieces.some(({ planned_distance }) => planned_distance);
        },

        hasDistancePiece(state, getters) {
            return getters.pieces.some(({ planned_time }) => planned_time);
        },

        hasSessionResults(state) {
            const { session } = state;
            return sessionHasResults(session) || sessionHasHrData(session) || sessionHasGpsData(session);
        },

        hasPeachFiles(state, getters) {
            return getters.crewFiles.some(crew => get(crew, 'telemetry_files.data'));
        },

        crewFiles(state) {
            return get(state, 'session.session_files.data.crews.data') || [];
        },

        crewsSessionFiles(state, getters, rootState, rootGetters) {
            const filterCrews = rootGetters['sessionResultsFilter/selectedCrews'];
            return getters.crewFiles.filter(({ id }) => isCrewSelected(filterCrews, id));
        },

        crewsSessionFilesGps(state, getters) {
            return getters.crewsSessionFiles.filter(crew => get(crew, 'session_crew_files.data.gps_file'));
        },

        crewsSessionPeachFiles(state, getters, rootState, rootGetters) {
            const filterCrews = rootGetters['sessionResultsFilter/selectedCrews'];
            const filteredCrews = getters.crewFiles.filter(crew =>
                isCrewSelected(filterCrews, crew.id) && 
                get(crew, 'telemetry_files.data')
            );
            
            // If filterCrews is empty, return as is
            if (!filterCrews.length) {
                return filteredCrews;
            }
            
            // Sort based on filterCrews order
            return filteredCrews.sort((a, b) => {
                const indexA = filterCrews.findIndex(c => c.id === a.id);
                const indexB = filterCrews.findIndex(c => c.id === b.id);
                return indexA - indexB;
            });
        },

        alternatives(state) {
            const alternatives = get(state, 'session.alternative_results.data') || [];
            return _.sortBy(alternatives, 'full_name');
        },

        alternativePiecesWithErgstickFiles(state, getters) {
            const pieces = [];
            getters.alternatives.forEach(user => {
                const userPieces = get(user, 'sport.data[0].pieces.data') || [];
                pieces.push(...userPieces.map(({ id }) => id));
            });
            return [...new Set(pieces)];
        },

        sportUserResultsUsers(state) {
            return get(state, 'session.sport_user_results.data[0].users.data') || [];
        },

        sessionFilesUsers(state) {
            const users = get(state, 'session.session_files.data.users.data') || [];
            return _.sortBy(users, 'full_name');
        },

        sportCrewResultsCrews(state) {
            return get(state, 'session.sport_crew_results.data[0].crews.data') || [];
        },

        sportCrewResultsCrewsByParent(state, getters) {
            return _.uniqBy(getters.sportCrewResultsCrews, 'parent_id');
        },

        participants(state) {
            const participants = get(state, 'session.participants.data') || [];
            return _.sortBy(participants, 'user.data.full_name');
        },

        sessionAthletes(state, getters) {
            return getters.participants.filter(({ user_role_id }) => isAthlete(user_role_id));
        },

        attendanceRoleId(state) {
            return get(state, 'session.attendance.data.user_role_id');
        },

        userIsAthlete(state, getters) {
            return isAthlete(getters.attendanceRoleId);
        },

        userIsCoach(state, getters) {
            return isCoach(getters.attendanceRoleId);
        },

        athletesHrFiles(state) {
            return _.sortBy(state.athletesHrFiles, 'full_name');
        },

        filteredAthletesHrFiles(state, getters, rootState, rootGetters) {
            const filterAthletes = rootGetters['sessionResultsFilter/selectedAthletes'];
            return getters.athletesHrFiles.filter(({ id }) => isAthleteSelected(filterAthletes, id));
        },

        athletesGpsFiles(state) {
            return _.sortBy(state.athletesGpsFiles, 'full_name');
        },

        filteredAthletesGpsFiles(state, getters, rootState, rootGetters) {
            const filterAthletes = rootGetters['sessionResultsFilter/selectedAthletes'];
            return getters.athletesGpsFiles.filter(
                ({ id }) =>
                    isAthleteSelected(filterAthletes, id) &&
                    !getters.alternatives.some(alternative => alternative.id === id)
            );
        },

        crewsGpsFiles(state, getters, rootState, rootGetters) {
            const filterCrews = rootGetters['sessionResultsFilter/selectedCrews'];
            const crews = state.crewsGpsFiles.filter(({ id }) => isCrewSelected(filterCrews, id));
            return _.sortBy(crews, 'name');
        },

        crewsPeachFiles(state) {
            return state.crewsPeachFiles;
        },

        sessionTelemetryAthletes(state, getters) {
            const athletes = [];

            getters.crewsSessionPeachFiles.forEach(crew => {
                const telemetryFile = crew.telemetry_files.data;
                const rowers = get(telemetryFile, 'roster_info.rowers') || Array(crew.boat_type.data.number_of_athletes).fill({});
                let seatNumber = 1;
                rowers.forEach(rower => {
                    let athleteName = '';
                    const crewUser = crew.crew_users.data.find(crewUser => crewUser.position === seatNumber);
                    if (crewUser)
                    {
                        athleteName = crewUser.user.data.full_name;
                    } else {
                        athleteName = rower.longName;
                        if (!rower.longName)
                        {
                            athleteName = i18n.t('Seat') + ' ' + seatNumber;
                        }
                    }

                    athletes.push({
                        crew_id: crew.id,
                        crew_name: crew.name,
                        user_id: `${crew.id}_${seatNumber}`,
                        full_name: athleteName,
                        seat: seatNumber
                    });
                    seatNumber++;
                });

                const cox = get(telemetryFile, 'roster_info.cox');
                if (cox > 0) {
                    let athleteName = '';
                    const crewUser = crew.crew_users.data.find(crewUser => crewUser.position === seatNumber);
                    if (crewUser)
                    {
                        athleteName = crewUser.user.data.full_name;
                    } else {
                        athleteName = cox.longName;
                        if (!cox.longName)
                        {
                            athleteName = i18n.t('Cox') + ' ' + seatNumber;
                        }
                    }

                    athletes.push({
                        user_id: `${crew.id}_${seatNumber}`,
                        full_name: athleteName,
                        seat: seatNumber
                    });
                }
            });

            return _.uniqBy(athletes, 'user_id');
        },

        sessionTelemetryAthletesGroupedByCrew(state, getters) {
            return getters.sessionTelemetryAthletes.reduce((crews, athlete) => {
                const crew = crews.find(crew => crew.title === athlete.crew_name);
                if (crew) {
                    crew.options.push(athlete);
                } else {
                    crews.push({
                        title: athlete.crew_name,
                        options: [athlete]
                    });
                }
                return crews;
            }, []);
        },

        individualUserId(state) {
            return state.individualUserId;
        }
    },

    mutations: {
        resetState(state) {
            state.session = null;
            state.athletesHrFiles = [];
            state.athletesGpsFiles = [];
            state.crewsGpsFiles = [];
            state.crewsPeachFiles = [];
            state.crewsDataTimestamps = {};
            state.individualUserId = null;
        },

        fillSession(state, payload) {
            state.session = Object.freeze(payload);
        },

        fillAthletesHrFiles(state, hrFile) {
            state.athletesHrFiles.push(hrFile);
        },

        fillAthletesGpsFiles(state, gpsFile) {
            state.athletesGpsFiles.push(gpsFile);
        },

        fillCrewsGpsFiles(state, gpsFile) {
            state.crewsGpsFiles.push(gpsFile);
        },

        setIndividualUser(state, userId) {
            state.individualUserId = userId;
        },

        fillCrewsPeachFiles(state, { crewId }) {
            const crews = get(state, 'session.session_files.data.crews.data') || [];
            const crew = crews.find(c => c.id === crewId);
            if (crew) {
                // Use Vue.set to ensure reactivity
                Vue.set(state.crewsDataTimestamps, crewId, Date.now());
            }
        }
    }
};

export default sessionResults;
